import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import reportScheduleService from '@api/v1/reportScheduleService'
import { fmtWhenInZone } from '@components/reports/queue/queueFormat'
import useReadState from '@hooks/useReadState'

/**
 * A report pack's cadence (spec 172): how often, which days, what time.
 *
 * Why not `RecurrenceEditor`: that editor always renders a "Date range each run" section,
 * which means nothing for a pack (each member keeps the date window of the schedule it was
 * copied from), and adding a prop to hide it means editing `components/reports/queue/**`,
 * which another session holds. So this is the editor's PRESENTATION only, for the subset a
 * pack needs. The calendar itself is not reimplemented: the next runs below come from the
 * same server preview (`/reports/schedules/preview`) the cron's engine answers, so what this
 * shows is what will fire.
 */

const DAYS = [
  { n: 1, short: 'Mon', long: 'Monday' }, { n: 2, short: 'Tue', long: 'Tuesday' },
  { n: 3, short: 'Wed', long: 'Wednesday' }, { n: 4, short: 'Thu', long: 'Thursday' },
  { n: 5, short: 'Fri', long: 'Friday' }, { n: 6, short: 'Sat', long: 'Saturday' },
  { n: 0, short: 'Sun', long: 'Sunday' },
]

const pad = (n) => String(n).padStart(2, '0')

const PackCadenceFields = ({ value, onChange, idPrefix = 'pack-cadence' }) => {
  const rule = value
  const set = (patch) => onChange({ ...rule, ...patch })

  const [debounced, setDebounced] = useState(rule)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(rule), 300)
    return () => clearTimeout(t)
  }, [rule])
  const key = useMemo(() => JSON.stringify(debounced), [debounced])
  const previewQuery = useQuery({
    queryKey: ['pack-cadence-preview', key],
    queryFn: () => reportScheduleService.preview({ recurrence: debounced, rollForward: 'pinned', count: 3 }),
    staleTime: 60_000,
  })
  const previewState = useReadState('pack-cadence-preview', previewQuery)
  const runs = previewState === 'ready' ? (previewQuery.data?.runs || []) : []

  const toggleDay = (n) => {
    const days = new Set(rule.byDay || [])
    if (days.has(n)) days.delete(n); else days.add(n)
    // At least one day: an empty weekly rule never fires.
    if (days.size) set({ byDay: [...days].sort((a, b) => a - b) })
  }

  return (
    <fieldset className="space-y-3">
      <legend className="mb-1.5 text-[11px] font-semibold text-rc-ink">When it runs</legend>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={`${idPrefix}-freq`} className="mb-1 block text-[11.5px] text-gray-600">Repeat</label>
          <select
            id={`${idPrefix}-freq`}
            value={rule.freq}
            onChange={(e) => set({ freq: e.target.value })}
            className="select-rc"
          >
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
          </select>
        </div>
        {rule.freq === 'monthly' && (
          <div>
            <label htmlFor={`${idPrefix}-mday`} className="mb-1 block text-[11.5px] text-gray-600">On day</label>
            <select
              id={`${idPrefix}-mday`}
              value={String(rule.byMonthDay ?? 1)}
              onChange={(e) => set({ byMonthDay: Number(e.target.value) })}
              className="select-rc"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              <option value={-1}>Last day</option>
            </select>
          </div>
        )}
        <div>
          <label htmlFor={`${idPrefix}-time`} className="mb-1 block text-[11.5px] text-gray-600">At</label>
          <input
            id={`${idPrefix}-time`}
            type="time"
            value={`${pad(rule.hour ?? 7)}:${pad(rule.minute ?? 0)}`}
            onChange={(e) => {
              const [h, m] = String(e.target.value || '07:00').split(':').map(Number)
              if (Number.isFinite(h) && Number.isFinite(m)) set({ hour: h, minute: m })
            }}
            className="input-rc"
          />
        </div>
      </div>
      {rule.freq === 'weekly' && (
        <div role="group" aria-labelledby={`${idPrefix}-days-label`}>
          <span id={`${idPrefix}-days-label`} className="mb-1 block text-[11.5px] text-gray-600">On</span>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const on = (rule.byDay || []).includes(d.n)
              return (
                <button
                  key={d.n}
                  type="button"
                  aria-pressed={on}
                  aria-label={d.long}
                  onClick={() => toggleDay(d.n)}
                  className={on
                    ? 'rounded-md border border-cb-accent-text bg-cb-accent-text px-2.5 py-1 text-[12px] font-semibold text-white'
                    : 'rounded-md border border-rc-control bg-white px-2.5 py-1 text-[12px] text-gray-800'}
                >
                  {d.short}
                </button>
              )
            })}
          </div>
        </div>
      )}
      <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5" data-testid="pack-cadence-preview">
        <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-gray-600">Next runs ({rule.tz})</div>
        {previewState === 'loading' || previewState === 'idle' ? (
          <p className="text-[12px] text-gray-600">Working out the next runs…</p>
        ) : previewState === 'ready' && runs.length ? (
          <ul className="space-y-0.5 text-[12px] text-gray-800">
            {runs.map((r) => <li key={r.at}>{fmtWhenInZone(r.at, rule.tz)}</li>)}
          </ul>
        ) : previewState === 'ready' ? (
          <p className="text-[12px] text-gray-700">This cadence never runs. Pick at least one day.</p>
        ) : (
          <p className="text-[12px] text-red-800">We couldn&apos;t work out when this will run, so nothing below is a promise. Try again in a moment.</p>
        )}
      </div>
    </fieldset>
  )
}

export default PackCadenceFields
